// @ts-nocheck
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { hasChanges } from "@insite/client-framework/Store/Pages/AccountSettings/AccountSettingsSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import setInitialValues from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SetInitialValues";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    account: state.pages.accountSettings.editingAccount,
    hasChanges: hasChanges(state),
    hasEmail: state.pages.accountSettings.editingAccount?.email,
    useDefaultCustomer: state.pages.accountSettings.useDefaultCustomer,
    selectedShipToId: state.pages.accountSettings.selectedShipToId,
    selectedBillToId: state.pages.accountSettings.selectedBillToId,
});

const mapDispatchToProps = {
    saveCurrentAccount,
    setInitialValues,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AccountSettingsFormStyles {
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const accountSettingsStyles: AccountSettingsFormStyles = {
    cancelButton: { variant: "secondary" },
};

const styles = accountSettingsStyles;

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const AccountSettingsFormContext = React.createContext<ContextType>({
    validators: {},
});

const AccountSettingsForm = ({
    account,
    hasChanges,
    id,
    hasEmail,
    saveCurrentAccount,
    setInitialValues,
    useDefaultCustomer,
    selectedBillToId,
    selectedShipToId,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const previousUseDefaultCustomer = usePrevious(useDefaultCustomer);
    const previousSelectedShipToId = usePrevious(selectedShipToId);
    const previousSelectedBillToId = usePrevious(selectedBillToId);
    const previousSubscriptionStatus = usePrevious(account?.isSubscribed);
    const trackSettingsChanged = ga.use.trackSettingsChanged();

    const [validators] = useState<{ [key: string]: Validator | undefined }>({});

    const updateSettingsHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();

        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        if (hasEmail) {
            setIsSubmitting(true);
            saveCurrentAccount({
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Settings updated"),
                        messageType: "success",
                        // Store Custom Code
                        "data-test-selector": "toast_settings_updated",
                    });
                    setIsSubmitting(false);
                    trackSettingsChanged({
                        subscription_status_changed: previousSubscriptionStatus !== account?.isSubscribed,
                        defaults_changed:
                            previousSelectedShipToId !== selectedShipToId ||
                            previousSelectedBillToId !== selectedBillToId ||
                            previousUseDefaultCustomer !== useDefaultCustomer,
                    });
                },
                onError: (message: any) => {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                    setIsSubmitting(false);
                },
            });
        }
    };

    return (
        <DataViewLoadingOverlay loading={isSubmitting} loadComplete={Boolean(account)}>
            <AccountSettingsFormContext.Provider value={{ validators }}>
                <FormWrapper>
                    <Zone contentId={id} zoneName="AccountSettingsForm" />
                    <FormActions>
                        <Button
                            {...styles.saveButton}
                            onClick={updateSettingsHandler}
                            data-test-selector="accountSettings_save"
                        >
                            {translate("Save")}
                        </Button>
                        {/* Store Removed - cancelButton */}
                    </FormActions>
                </FormWrapper>
            </AccountSettingsFormContext.Provider>
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsForm),
    definition: {
        group: "Account Settings",
        allowedContexts: ["AccountSettingsPage"],
        displayName: "Form",
    },
};

export default widgetModule;
